<script setup lang="ts">
type DetailTab = {
  key: string
  label: string
  localeKey?: string
}

const props = withDefaults(
  defineProps<{
    tabs: DetailTab[]
    activeKey: string
    exchangeName?: string
    exchangeId?: string
  }>(),
  {
    exchangeName: '',
    exchangeId: '',
  },
)

const emit = defineEmits<{
  select: [key: string]
}>()

const { t } = useAppI18n()

const getTabLabel = (tab: DetailTab) => {
  if (!tab.localeKey) {
    return tab.label
  }

  const translatedLabel = t(tab.localeKey)

  return translatedLabel === tab.localeKey ? tab.label : translatedLabel
}

const matomo = useMatomo()
const handleSelect = (tab: DetailTab) => {
  matomo.trackEvent('broker_column', {
    af_type: getTabLabel(tab),
    af_type1: tab.key,
    af_name: props.exchangeId || '',
    af_code: props.exchangeName || '',
  })
  emit('select', tab.key)
}
</script>

<template>
  <nav class="exchange-detail-tabs" aria-label="交易所详情导航">
    <button
      v-for="tab in tabs"
      :key="tab.key"
      type="button"
      class="exchange-detail-tab"
      :class="{ 'exchange-detail-tab-active': activeKey === tab.key }"
      :aria-current="activeKey === tab.key ? 'true' : undefined"
      :data-exchange-detail-tab="tab.key"
      @click="handleSelect(tab)"
    >
      {{ getTabLabel(tab) }}
    </button>
  </nav>
</template>

<style scoped lang="less">
.exchange-detail-tabs {
  position: sticky;
  top: 70px;
  z-index: 15;
  display: flex;
  width: 100%;
  height: 60px;
  align-items: stretch;
  gap: 24px;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid var(--color-border-default, rgba(5, 21, 36, 0.06));
  background: #ffffff;
  scrollbar-width: none;
}

.exchange-detail-tabs::-webkit-scrollbar {
  display: none;
}

.exchange-detail-tab {
  position: relative;
  display: inline-flex;
  height: 60px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 0;
  background: transparent;
  color: var(--color-text-subtlest, #7d818a);
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  padding: 16px 0;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease;
}

.exchange-detail-tab:hover,
.exchange-detail-tab:focus-visible,
.exchange-detail-tab-active {
  color: var(--color-text, #292a2e);
}

.exchange-detail-tab:focus-visible {
  outline: 2px solid rgba(36, 104, 222, 0.3);
  outline-offset: -2px;
}

.exchange-detail-tab::after {
  position: absolute;
  bottom: 2%;
  left: 50%;
  width: 28px;
  height: 4px;
  content: '';
  background: var(--color-icon-default, #292a2e);
  border-radius: 999px;
  opacity: 0;
  transform: translateX(-50%) scaleX(0.5);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.exchange-detail-tab-active {
  font-weight: 600;
}

.exchange-detail-tab-active::after {
  opacity: 1;
  transform: translateX(-50%) scaleX(1);
}

@media screen and (min-width: 800px) and (max-width: 1199px) {
  .exchange-detail-tabs {
    gap: 24px;
  }
}

@media screen and (max-width: 799px) {
  .exchange-detail-tabs {
    position: static;
    gap: 20px;
  }
}
</style>
